import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { memo } from "react";

import {
  ClinicCard,
  ClinicCardContactInfo,
  ClinicCardContent,
  ClinicCardName,
  ClinicMapsButton,
} from "../styles/NetworkClinicCardStyles";
import type { NetworkClinic } from "./NetworkForm/networkFormTypes";

type NetworkClinicCardProps = {
  data?: NetworkClinic[];
};

const EMPTY_VALUE = "Não informado";

const getDisplayValue = (value?: string | number | null): string => {
  if (value === undefined || value === null || value === "") {
    return EMPTY_VALUE;
  }

  return String(value);
};

const formatPhones = (
  phones?: string | number | Array<string | number>
): string => {
  if (Array.isArray(phones)) {
    return phones.length > 0 ? phones.map(String).join(" / ") : EMPTY_VALUE;
  }

  return getDisplayValue(phones);
};

const buildAddress = (item: NetworkClinic): string => {
  return [
    item.nome_credenciado,
    item.rua,
    item.bairro,
    item.cidade && item.uf ? `${item.cidade} - ${item.uf}` : item.cidade,
    item.cep,
  ]
    .filter(Boolean)
    .join(", ");
};

const buildGoogleMapsUrl = (address: string): string => {
  const formattedAddress = encodeURIComponent(address);

  return `https://www.google.com/maps/search/?api=1&query=${formattedAddress}`;
};

const NetworkingClinicCard = ({ data = [] }: NetworkClinicCardProps) => {
  const handleMapRedirect = (item: NetworkClinic) => {
    const address = buildAddress(item);
    const mapUrl = buildGoogleMapsUrl(address);

    window.open(mapUrl, "_blank", "noopener,noreferrer");
  };

  if (!data.length) {
    return (
      <Grid size={{ xs: 12 }} paddingX={{ xs: 3, sm: 4 }} paddingY={1.5}>
        <Typography variant="body1">
          Nenhum credenciado encontrado para a consulta.
        </Typography>
      </Grid>
    );
  }

  return (
    <>
      {data.map((item, index) => {
        const clinicName = getDisplayValue(item.nome_credenciado);

        const key = [
          item.codigo_credenciado,
          item.codigo_local,
          item.nome_credenciado,
          item.areaAtuacao,
          index,
        ]
          .filter(Boolean)
          .join("_");

        return (
          <Grid
            key={key}
            size={{ xs: 12, sm: 12, md: 6 }}
            paddingX={{ xs: 3, sm: 4 }}
            paddingY={1.5}
          >
            <ClinicCard>
              <ClinicCardContent>
                <ClinicCardName>Credenciado: {clinicName}</ClinicCardName>

                <ClinicCardContactInfo>
                  CRO: {getDisplayValue(item.cro)}
                </ClinicCardContactInfo>

                <ClinicCardContactInfo>
                  Área de Atuação: {getDisplayValue(item.areaAtuacao)}
                </ClinicCardContactInfo>

                <ClinicCardContactInfo>
                  Especialidade: {getDisplayValue(item.especialidade)}
                </ClinicCardContactInfo>

                <ClinicCardContactInfo>
                  Endereço: {getDisplayValue(item.rua)}
                </ClinicCardContactInfo>

                <ClinicCardContactInfo>
                  Bairro: {getDisplayValue(item.bairro)}, Cidade:{" "}
                  {getDisplayValue(item.cidade)} - {getDisplayValue(item.uf)},
                  Cep: {getDisplayValue(item.cep)}
                </ClinicCardContactInfo>

                <ClinicCardContactInfo>
                  Telefone(s): {formatPhones(item.telefones)}
                </ClinicCardContactInfo>
              </ClinicCardContent>

              <ClinicMapsButton
                variant="contained"
                fullWidth
                aria-label={`Ver ${clinicName} no mapa`}
                onClick={() => handleMapRedirect(item)}
              >
                ver no mapa
              </ClinicMapsButton>
            </ClinicCard>
          </Grid>
        );
      })}
    </>
  );
};

export default memo(NetworkingClinicCard);